import { useEffect, useState, type ReactNode } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DeleteOutline as DeleteOutlineIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material'

import { DialogHeader } from '@/components/atoms/DialogHeader'
import { RecordingControl } from '@/apps/latest/components/organisms/MeetingBlock/RecordingControl'
import type { RecordingViewModel } from '@/apps/latest/shared/recording-status/types'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import {
  RECORDING_CTA_GLYPH_PX,
  RECORDING_CTA_OUTLINE,
  RECORDING_CTA_SIZE,
} from '@/apps/latest/shared/styles/recordingCta'
import {
  NOTION_BUTTON_PRIMARY_SX,
  NOTION_BUTTON_TEXT_SX,
} from '@/shared/styles/buttonStyles'
import { NOTION_DIALOG_PAPER_SX } from '@/shared/styles/dialogStyles'
import { RADIUS } from '@/shared/styles/radii'

// 破棄アイコンの意匠. 録音中 (ADR-0038) と一時停止中 (ADR-0018) の 2 面で完全同一に
// 保つため、 片方だけが動かないよう 1 か所に置く (ADR-0038 決定 1).
const DISCARD_ICON_BUTTON_SX = {
  width: 40,
  height: 40,
  mr: 1,
  color: colorTokens.fg.muted,
  '&:hover': { color: colorTokens.state.dangerIcon },
} as const

// viewStatus → 見出し行の脇に出す inline caption (= 生成中 / 完了 / 失敗 / paused 系のみ).
// 録音中 / 録音前 / 停止処理中は MeetingBlock 側で表現するため、 ここでは出さない.
// PatientInfoBar (患者名行) とまとめて録音 (見出し行) が同じ文言を出す (ADR-0040 決定 5).
export const recordingStatusCaption = (
  recordingStatus: RecordingViewModel
): string | null => {
  const engineState = recordingStatus.engine.state
  switch (recordingStatus.status) {
    case 'paused':
      // in-flight (保存中 / 再開中) を出し分ける (log/185: アップロード中の可視化).
      if (engineState === 'pausing') return '録音を保存中…'
      if (engineState === 'resuming') return '再開中…'
      return '一時停止中'
    case 'generating':
      return 'SOAP 生成中…'
    case 'completed':
      return '完了'
    case 'failed':
      return '失敗'
    default:
      return null
  }
}

interface Props {
  // page で組み立てた L3 view-model. CTA の露出と操作の駆動.
  recordingStatus: RecordingViewModel
  // 録音 CTA (mic) click と ⇧Enter の開始.
  onStartRecording: () => void
  // idle のときだけ主役 mic の左に並べる選択群 (録音モード / 担当 / 文脈 等). caller が組む.
  idleControls?: ReactNode
  // 一時停止の入口を出すか. まとめて録音は復元 (adoptPausedSession) が単一 event 前提で、
  // 一時停止した瞬間に reload / 患者切替で再開も再 start もできなくなるため出さない
  // (ADR-0040 決定 3). paused 側の UI (再開 / 完了 / 破棄) は残す = 到達しないだけ.
  allowPause?: boolean
  // idle の mic を disabled にする (まとめて録音: 患者行が揃うまで). ⇧Enter の開始も止める.
  startDisabled?: boolean
  /**
   * 一時停止中の「完了」 confirm の本文。既定は薬剤師の文言 (SOAP を生成する)。
   * 録音の終端で何が作られるかは呼び出し側の系統が決めるので、 文言だけ差し替えられる。
   */
  finishConfirmText?: string
}

const DEFAULT_FINISH_CONFIRM_TEXT =
  'ここまでの録音内容で SOAP を生成します。完了すると録音の再開はできません。'

// 録音の開始から終端までの操作を 1 つにまとめた部品 (ADR-0040).
// 患者ヘッダ (PatientInfoBar) とまとめて録音の見出し行が同じものを置く = 録音の終端操作は
// 1 部品で、 患者ヘッダの有無と独立. 意匠の値はここでは変えず、 design-book の
// 02-components/recording-cta-cluster.md と 04-apps/kakeru.md (録音一時停止 / 録音中の破棄) に従う.
//   idle:       [idleControls] [mic]
//   recording:  [破棄] [一時停止] [■]          ← 破棄は主役から最も遠い左端
//   paused:     [破棄] [完了] [再開]
//   in-flight:  [ring + ⏸ / ▶]
export const RecordingCtaCluster = ({
  recordingStatus,
  onStartRecording,
  idleControls,
  allowPause = true,
  startDisabled = false,
  finishConfirmText = DEFAULT_FINISH_CONFIRM_TEXT,
}: Props) => {
  // 一時停止中の「完了」 confirm (log/185): 完了 = SOAP 生成の確定で再開できなくなるため、
  // paused からの完了だけは誤操作ガードを挟む (録音中の停止は従来どおり即時).
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false)
  // 「破棄」 confirm (ADR-0018 / ADR-0038): 録音を捨てる非可逆操作のため 1 行 confirm を挟む.
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)

  const engineState = recordingStatus.engine.state
  const isRecordingState =
    recordingStatus.status === 'recording' ||
    recordingStatus.status === 'stopping'

  // 破棄導線が出ている面 (= confirm を開いてよい面). 録音中と settled paused
  // (ADR-0018) の 2 つ. Dialog を CTA 行の末尾へ出して 2 面で共用した結果、
  // 「confirm を開いている間に面が消える」 が起こりうるようになったため、 面の条件を
  // 1 本の式に括って confirm の生存条件にも使う.
  //
  // 露出は status だけで決める (ADR-0039). **録音開始の後から終端までは常に出す**:
  // 「しっかり文字起こし」 (batch) は streaming session を張らず破棄はローカルで閉じるだけ、
  // 低速 fallback へ降格した後も back の cancel が転送路の状態に依存せず原状復帰する.
  // 経路の健全性は破棄の可否と関係がない.
  const canDiscardRecording = recordingStatus.status === 'recording'
  const canDiscard =
    canDiscardRecording ||
    (recordingStatus.status === 'paused' &&
      engineState !== 'pausing' &&
      engineState !== 'resuming')

  // 開いている間に面が消えたら (停止して generating に入った / failed で片付いた / paused の
  // in-flight) confirm も畳む. 対象の無い破棄を押させないためと、 次にこの面が戻ったとき
  // (同じ event で録り直す) に confirm が勝手に開かないため. 面との積で「見えなく」するだけだと
  // state に true が残る (= "Adjusting state when props change").
  if (confirmDiscardOpen && !canDiscard) {
    setConfirmDiscardOpen(false)
  }

  // ⇧Enter で録音開始 / 停止をトグルする (= マウスを使わず手元のまま操作).
  // RecordingControl が出る idle / recording のときだけ意味を持つ. 入力欄 (textarea /
  // input / contenteditable) に focus 中は通常の改行を優先し奪わない.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.shiftKey || e.key !== 'Enter' || e.repeat) return
      // confirm 表示中はショートカットを無効化する (log/185 検証 F9:
      // ダイアログ背後で resume が発火し、 確認中のダイアログごと録音再開してしまう).
      if (confirmFinishOpen || confirmDiscardOpen) return
      const t = e.target as HTMLElement | null
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.isContentEditable)
      )
        return
      if (recordingStatus.status === 'idle') {
        // mic が disabled の間 (患者行の取得中) は手元操作でも始めない.
        if (startDisabled) return
        e.preventDefault()
        onStartRecording()
      } else if (recordingStatus.status === 'recording') {
        e.preventDefault()
        recordingStatus.stopRecording()
      } else if (recordingStatus.showResumeCta) {
        // 一時停止中 (log/185) は ⇧Enter で再開 (= 開始/停止と同じ手元操作の流儀).
        e.preventDefault()
        recordingStatus.resumeRecording()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    recordingStatus,
    onStartRecording,
    startDisabled,
    confirmFinishOpen,
    confirmDiscardOpen,
  ])

  return (
    // 操作群は密に寄せる (= 見出しとの間隔だけ広く、 control 同士は近接).
    // 狭幅でも録音 CTA を潰さないよう flexShrink:0 で固定し、 左の見出し側を縮める.
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
    >
      {/* idle 時のみ録音前の選択群を mic 左に並べる (Gemini 流). 中身は caller が組む. */}
      {recordingStatus.status === 'idle' && idleControls}
      {/* 録音中の破棄 (ADR-0038 / ADR-0039). 停止 (= SOAP を作る) と並ぶ第 2 の終端で、
          SOAP を作らずに捨てて event を PENDING に戻す (= その場で録り直せる).
          露出は status だけで決める (経路の健全性は条件にしない).
          破棄専用の derive フラグは持たない (ADR-0018 と同じ引き算).
          主役 CTA (停止) から最も遠い左端に置き、 gap + confirm の二重で誤タップを防ぐ
          (paused の [破棄][完了][再開] と同じ文法). */}
      {canDiscardRecording && (
        <Tooltip title="録音を破棄" placement="bottom">
          <span>
            <IconButton
              aria-label="録音を破棄"
              onClick={() => setConfirmDiscardOpen(true)}
              sx={DISCARD_ICON_BUTTON_SX}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {/* 一時停止 (log/185). streaming 経路が健在な録音中のみ. 停止 (■) の左に置き、
          「完了とは別の操作」 であることを位置と icon で示す. */}
      {allowPause && recordingStatus.showPauseCta && (
        <Tooltip title="一時停止" placement="bottom">
          <span>
            <IconButton
              aria-label="一時停止"
              onClick={() => recordingStatus.pauseRecording()}
              sx={{ width: 40, height: 40 }}
            >
              <PauseIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {/* pause/resume の in-flight はグレーアウトした操作ボタンではなくスピナーで示す
          (log/185: 保存中に何が起きているか分からない・押せない理由が見えない問題の対策).
          settled paused になったら [完了][再開] が活性で現れる. */}
      {recordingStatus.status === 'paused' &&
        (engineState === 'pausing' || engineState === 'resuming') && (
          <Tooltip
            title={
              engineState === 'pausing'
                ? 'ここまでの録音を保存しています…'
                : '録音を再開しています…'
            }
            placement="bottom"
          >
            {/* 汎用 spinner だけでは「どの操作の in-flight か」が消えるため、 ring の中に
                操作 glyph (pausing=pause / resuming=play) を重ねる (= アイコンでラベルを
                削る文法. 押した icon がその場で ring をまとい settled に着地する連続性).
                色は fg.muted (裏方作業であり CTA ではないので accent を使わない). */}
            <Box
              sx={{
                width: RECORDING_CTA_SIZE,
                height: RECORDING_CTA_SIZE,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: colorTokens.fg.muted,
              }}
            >
              <CircularProgress
                size={30}
                thickness={2.5}
                sx={{ color: 'inherit' }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {engineState === 'pausing' ? (
                  <PauseIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PlayArrowIcon sx={{ fontSize: 16 }} />
                )}
              </Box>
            </Box>
          </Tooltip>
        )}
      {/* 一時停止中 (settled): [破棄] を左端に、 [完了] をセカンダリに、 [再開] を右端に置く.
          再開は「録音を再び始める」操作なので、 録音開始 (mic) と同じ位置・同じ意匠
          (RECORDING_CTA_* 共有定数・accent 色・hover overlay) にして、 主役 CTA の
          一貫性を保つ.
          破棄 (ADR-0018) は paused の第 3 の終端 (sweeper が PAUSED を恒久除外するため、
          再開/完了/破棄の人間の 3 択だけが終端手段). 主役の再開から最も遠い左端に置き、
          完了との間の gap + confirm の二重で誤タップを防ぐ. confirm は録音中の破棄と
          意味論が同じなので CTA 行末尾で共用する. */}
      {recordingStatus.status === 'paused' &&
        engineState !== 'pausing' &&
        engineState !== 'resuming' && (
          <>
            <Tooltip title="録音を破棄" placement="bottom">
              <span>
                <IconButton
                  aria-label="録音を破棄"
                  disabled={!recordingStatus.showResumeCta}
                  onClick={() => setConfirmDiscardOpen(true)}
                  sx={DISCARD_ICON_BUTTON_SX}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="録音を完了" placement="bottom">
              <span>
                <IconButton
                  aria-label="録音を完了"
                  disabled={!recordingStatus.showResumeCta}
                  onClick={() => setConfirmFinishOpen(true)}
                  sx={{ width: 40, height: 40, color: colorTokens.fg.muted }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 13,
                      height: 13,
                      borderRadius: `${RADIUS.sm}px`,
                      bgcolor: 'currentColor',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box component="span">録音を再開</Box>
                  <Box component="span" sx={{ opacity: 0.7, fontSize: 11 }}>
                    ⇧Enter
                  </Box>
                </Box>
              }
              placement="bottom"
            >
              <span>
                <IconButton
                  aria-label="録音を再開"
                  disabled={!recordingStatus.showResumeCta}
                  onClick={() => recordingStatus.resumeRecording()}
                  sx={{
                    // 録音開始 mic と同意匠 (寸法・輪郭は RECORDING_CTA_* 共有定数に追従)
                    width: RECORDING_CTA_SIZE,
                    height: RECORDING_CTA_SIZE,
                    flexShrink: 0,
                    color: colorTokens.accent.primary,
                    border: RECORDING_CTA_OUTLINE,
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: colorTokens.overlay.selected,
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'transparent',
                      color: colorTokens.fg.disabled,
                    },
                  }}
                >
                  <PlayArrowIcon sx={{ fontSize: RECORDING_CTA_GLYPH_PX }} />
                </IconButton>
              </span>
            </Tooltip>
            {/* 完了の confirm は DeleteRecordingDialog と同形 (paper SSoT +
                DialogHeader + 1 行本文. 02-components/dialog.md). */}
            <Dialog
              open={confirmFinishOpen}
              onClose={() => setConfirmFinishOpen(false)}
              maxWidth="xs"
              fullWidth
              sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
            >
              <DialogHeader
                title="録音を完了"
                onClose={() => setConfirmFinishOpen(false)}
              />
              <DialogContent sx={{ pt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ color: colorTokens.fg.default }}
                >
                  {finishConfirmText}
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button
                  onClick={() => setConfirmFinishOpen(false)}
                  variant="text"
                  size="small"
                  sx={NOTION_BUTTON_TEXT_SX}
                >
                  キャンセル
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={NOTION_BUTTON_PRIMARY_SX}
                  onClick={() => {
                    setConfirmFinishOpen(false)
                    recordingStatus.stopRecording()
                  }}
                >
                  完了する
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      {/* 主役の録音 CTA. idle = 録音開始 (mic), recording = 停止 (■) を同一位置でトグル.
          停止は MeetingTabBar に分離せず、 開始と同じこの control が担う. */}
      {(recordingStatus.status === 'idle' || isRecordingState) && (
        <RecordingControl
          status={recordingStatus.status}
          stream={recordingStatus.engine.stream}
          onStart={onStartRecording}
          onStop={() => recordingStatus.stopRecording()}
          disabled={startDisabled}
        />
      )}
      {/* 破棄の confirm は録音中 (ADR-0038) と一時停止中 (ADR-0018) で共用する.
          どちらも「SOAP を作らず捨てる」で意味論が同じため、 文言も状態で分岐しない. */}
      {canDiscard && (
        <Dialog
          open={confirmDiscardOpen}
          onClose={() => setConfirmDiscardOpen(false)}
          maxWidth="xs"
          fullWidth
          sx={{ '& .MuiDialog-paper': NOTION_DIALOG_PAPER_SX }}
        >
          <DialogHeader
            title="録音を破棄"
            onClose={() => setConfirmDiscardOpen(false)}
          />
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ color: colorTokens.fg.default }}>
              ここまでの録音がすべて削除され、元に戻せません。
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button
              onClick={() => setConfirmDiscardOpen(false)}
              variant="text"
              size="small"
              sx={NOTION_BUTTON_TEXT_SX}
            >
              キャンセル
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{
                ...NOTION_BUTTON_PRIMARY_SX,
                bgcolor: colorTokens.state.dangerFg,
                '&:hover': {
                  bgcolor: colorTokens.state.dangerFg,
                  filter: 'brightness(0.95)',
                  boxShadow: 'none',
                },
              }}
              onClick={() => {
                setConfirmDiscardOpen(false)
                void recordingStatus.discardRecording()
              }}
            >
              破棄
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  )
}
